import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import ProjectCard from "./components/ProjectCard";
import FilterPanel from "./components/FilterPanel";
import ProjectModal from "./components/ProjectModal";
import StatsOverview from "./components/StatsOverview";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useProjects } from "../../hooks/useProjects";

const ProjectPortfolio = () => {
  const navigate = useNavigate();
  const { projects: allProjects, loading, maxPossibleGen } = useProjects();
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: "",
    location: "",
    systemSize: "",
    minGen: 0,
    maxGen: 20000,
  });

  // Update maxGen filter when projects are loaded
  useEffect(() => {
    if (maxPossibleGen && filters.maxGen !== maxPossibleGen && filters.maxGen === 20000) {
      setFilters(prev => ({ ...prev, maxGen: maxPossibleGen }));
    }
  }, [maxPossibleGen]);

  const [filteredProjects, setFilteredProjects] = useState([]);

  // Filter projects based on current filters
  useEffect(() => {
    if (!allProjects) return;

    let filtered = [...allProjects];

    if (filters?.type) {
      filtered = filtered?.filter((project) => project?.tipo === filters?.type);
    }

    if (filters?.location) {
      filtered = filtered?.filter(
        (project) => project?.ubicacion === filters?.location
      );
    }

    if (filters?.systemSize) {
      filtered = filtered?.filter((project) => {
        const size = project?.generaDiaria || 0;
        switch (filters?.systemSize) {
          case "1-10":
            return size >= 1 && size <= 10;
          case "10-20":
            return size > 10 && size <= 20;
          case "20-30":
            return size > 20 && size <= 30;
          case "30-40":
            return size > 30 && size <= 40;
          case "40-50":
            return size > 40 && size <= 50;
          case "50+":
            return size > 50;
          default:
            return true;
        }
      });
    }

    // Filter by Monthly Generation Range
    filtered = filtered?.filter((project) => {
      const genMensual = project?.generaMensual || 0;
      const min = parseFloat(filters?.minGen) || 0;
      const max = parseFloat(filters?.maxGen) || Infinity;
      return genMensual >= min && genMensual <= max;
    });



    setFilteredProjects(filtered);
  }, [filters, allProjects]);

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleRequestQuote = (project) => {
    // Navigate to calculator with pre-filled data
    navigate("/calculadora", {
      state: {
        prefillData: {
          systemSize: project?.generaDiaria,
          propertyType: project?.tipo,
          location: project?.ubicacion,
        },
      },
    });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      type: "",
      location: "",
      systemSize: "",
      minGen: 0,
      maxGen: maxPossibleGen || 2000,
    });
  };

  const toggleFilterPanel = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando portafolio...</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Nuestros <span className="solar-text-gradient">Proyectos</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Descubre cómo hemos transformado hogares, industrias y comercios en
              toda Guatemala con soluciones solares personalizadas que generan
              ahorros reales e impacto ambiental positivo.
            </p>
          </div>

          {/* Quick Stats */}
          <StatsOverview projects={allProjects} />
        </div>
      </section>
      {/* Projects Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filter Sidebar */}
            <div className="lg:w-80 flex-shrink-0">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isOpen={isFilterOpen}
                onToggle={toggleFilterPanel}
                projectCount={filteredProjects?.length}
                maxLimit={maxPossibleGen}
              />
            </div>

            {/* Projects Grid */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    Proyectos Destacados
                  </h2>
                  <p className="text-muted-foreground">
                    {filteredProjects?.length} proyecto
                    {filteredProjects?.length !== 1 ? "s" : ""} encontrado
                    {filteredProjects?.length !== 1 ? "s" : ""}
                  </p>
                </div>

                <Button
                  variant="outline"
                  iconName="Calculator"
                  iconPosition="left"
                  onClick={() => navigate("/calculadora")}
                  className="hidden sm:flex"
                >
                  Calcular Mi Sistema
                </Button>
              </div>

              {/* Projects Grid */}
              {filteredProjects?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProjects?.map((project) => (
                    <ProjectCard
                      key={project?.id}
                      project={project}
                      onViewDetails={handleViewDetails}
                      onRequestQuote={handleRequestQuote}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon
                    name="Search"
                    size={48}
                    className="text-muted-foreground mx-auto mb-4"
                  />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No se encontraron proyectos
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Intenta ajustar los filtros para ver más resultados
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Limpiar Filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-accent/5 to-secondary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            ¿Listo para tu Proyecto Solar?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Únete a nuestros clientes satisfechos y comienza a ahorrar con
            energía solar limpia
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              onClick={() => navigate("/calculadora")}
              className="bg-cta-orange hover:bg-cta-orange/90"
            >
              Calcular Mi Ahorro
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              onClick={() => navigate("/nosotros")}
            >
              Contactar Experto
            </Button>
          </div>
        </div>
      </section>
      {/* Project Detail Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
        onRequestQuote={handleRequestQuote}
      />
    </div>
  );
};

export default ProjectPortfolio;
